import { z } from 'zod';
import { nonEmptyPatch } from './_patch.js';
import { phoneSchema } from './phone.js';
import { IssuedStorageObjectKeySchema } from './storage.js';

const CarrierPriceListObjectKeySchema = IssuedStorageObjectKeySchema.refine(
  (s) => s.startsWith('carrier-price-list/'),
  'object key must use carrier-price-list scope',
);

/**
 * @aiPurpose closed-set discriminator for a stored carrier price-list file.
 *   Optional on the wire: legacy rows persisted before this discriminator
 *   was introduced render as `image/*` by consumer convention. Backend MUST
 *   normalize DB `null` to omitted/undefined before parsing/serializing —
 *   sending an explicit `null` here is rejected so the discriminator stays
 *   strictly additive.
 */
export const CarrierPriceListFileContentTypeSchema = z.enum([
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
]);
export type CarrierPriceListFileContentType = z.infer<typeof CarrierPriceListFileContentTypeSchema>;

export const CarrierWarehouseSchema = z
  .object({
    id: z.string().uuid(),
    carrierId: z.string().regex(/^TR-\d{4}$/, 'must match TR-NNNN'),
    carrierName: z.string().min(1),
    name: z.string().min(1),
    city: z.string().min(1),
    description: z.string().min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type CarrierWarehouse = z.infer<typeof CarrierWarehouseSchema>;

export const CarrierPriceListSchema = z
  .object({
    id: z.string().uuid(),
    carrierId: z.string().regex(/^TR-\d{4}$/, 'must match TR-NNNN'),
    carrierName: z.string().min(1),
    name: z.string().min(1),
    imageObjectKey: CarrierPriceListObjectKeySchema,
    fileContentType: CarrierPriceListFileContentTypeSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type CarrierPriceList = z.infer<typeof CarrierPriceListSchema>;

export const CarrierPriceListCatalogItemSchema = CarrierPriceListSchema.extend({
  imageDownloadUrl: z.string().url(),
  imageDownloadUrlExpiresAt: z.string().datetime(),
}).strict();
export type CarrierPriceListCatalogItem = z.infer<typeof CarrierPriceListCatalogItemSchema>;

export const CarrierSchema = z
  .object({
    id: z.string().regex(/^TR-\d{4}$/, 'must match TR-NNNN'),
    name: z.string().min(1),
    email: z.string().email().nullable(),
    phone: phoneSchema.nullable(),
    notes: z.string(),
    active: z.boolean(),
    warehouses: z.array(CarrierWarehouseSchema),
    priceLists: z.array(CarrierPriceListSchema),
  })
  .strict();

export type Carrier = z.infer<typeof CarrierSchema>;

export const CreateCarrierInputSchema = CarrierSchema.omit({
  id: true,
  warehouses: true,
  priceLists: true,
})
  .extend({
    active: z.boolean().default(true),
  })
  .strict();
export type CreateCarrierInput = z.infer<typeof CreateCarrierInputSchema>;

// PATCH MUST NOT carry `active`. Lifecycle (deactivate / reactivate) is owned
// exclusively by DELETE: a soft-deleted carrier stays inactive and cannot be
// re-promoted via PATCH, and an active carrier is taken out of circulation
// only through `DELETE /carriers/:id` so the `carrier.deactivate` audit code
// always names that transition. Allowing `active` in PATCH would let
// `{active:true}` reactivate (forbidden) or `{active:false}` deactivate while
// auditing as `carrier.update`, splitting the lifecycle ledger.
export const PatchCarrierInputSchema = nonEmptyPatch(
  CreateCarrierInputSchema.omit({ active: true }).partial().strict(),
);
export type PatchCarrierInput = z.infer<typeof PatchCarrierInputSchema>;

export const CarrierWarehouseInputSchema = z
  .object({
    name: z.string().min(1),
    city: z.string().min(1),
    description: z.string().min(1),
  })
  .strict();
export type CarrierWarehouseInput = z.infer<typeof CarrierWarehouseInputSchema>;

export const PatchCarrierWarehouseInputSchema = nonEmptyPatch(
  CarrierWarehouseInputSchema.partial().strict(),
);
export type PatchCarrierWarehouseInput = z.infer<typeof PatchCarrierWarehouseInputSchema>;

export const CarrierPriceListInputSchema = z
  .object({
    name: z.string().min(1),
    imageObjectKey: CarrierPriceListObjectKeySchema,
    fileContentType: CarrierPriceListFileContentTypeSchema.optional(),
  })
  .strict();
export type CarrierPriceListInput = z.infer<typeof CarrierPriceListInputSchema>;

export const PatchCarrierPriceListInputSchema = nonEmptyPatch(
  CarrierPriceListInputSchema.partial().strict(),
);
export type PatchCarrierPriceListInput = z.infer<typeof PatchCarrierPriceListInputSchema>;

// `z.coerce.boolean()` is unsafe for query strings: it routes through
// `Boolean(value)`, which is `true` for any non-empty string — including
// `"false"`. We accept the literal strings `"true"` / `"false"` (or actual
// booleans, in case a downstream caller pre-parses) and reject anything
// else, so `?active=false` actually filters to inactive rows.
const stringBoolean = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

/**
 * @aiPurpose query string for `GET /api/carriers` — cursor-paginated catalog list.
 * @aiInvariant `.strict()` blocks unknown keys. `active` defaults to `true` so
 *   the picker omits soft-deleted rows by default; pass `active=false` (the
 *   literal string) to filter to inactive rows. `limit` is clamped to 1..100
 *   with default 50. `q` matches `name` ILIKE %q%, plus `phone` and `email`
 *   on exact match (case-insensitive for email — server lowercases before
 *   comparing). `cursor` is opaque base64url(`{createdAt, id}`) decoded
 *   server-side; tampering yields a 400.
 */
export const GetCarriersQuerySchema = z
  .object({
    q: z.string().min(1).max(200).optional(),
    active: stringBoolean.default(true),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type GetCarriersQuery = z.infer<typeof GetCarriersQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/carriers`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying query returned
 *   ≤ `limit` rows; otherwise it carries the encoded `(createdAt, id)` of the
 *   last returned row.
 */
export const GetCarriersResponseSchema = z
  .object({
    items: z.array(CarrierSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetCarriersResponse = z.infer<typeof GetCarriersResponseSchema>;

export const CarrierWarehousesQuerySchema = z
  .object({
    q: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(1000),
  })
  .strict();
export type CarrierWarehousesQuery = z.infer<typeof CarrierWarehousesQuerySchema>;

export const CarrierWarehousesResponseSchema = z
  .object({
    items: z.array(CarrierWarehouseSchema),
  })
  .strict();
export type CarrierWarehousesResponse = z.infer<typeof CarrierWarehousesResponseSchema>;

export const CarrierPriceListsQuerySchema = CarrierWarehousesQuerySchema;
export type CarrierPriceListsQuery = z.infer<typeof CarrierPriceListsQuerySchema>;

export const CarrierPriceListsResponseSchema = z
  .object({
    items: z.array(CarrierPriceListCatalogItemSchema),
  })
  .strict();
export type CarrierPriceListsResponse = z.infer<typeof CarrierPriceListsResponseSchema>;
